import { EMT_PAGE_NAMES, PAGE_TYPE } from '../../constants';
import { getCurrentPromoImageSrc } from '../../helpers/pps-helpers';
import { buildPagePerStoreBreadcrumbs } from '../../structured-data/build-pps-breadcrumbs';
import { buildPagePerStoreStructuredFAQ } from '../../structured-data/build-pps-structured-faq';

/**
 * Generates the description for the Rentals detail page.
 *
 * @param {Object} params - Parameters for generating the description.
 * @param {string} params.storeName - The name of the store.
 * @param {string} params.city - The city where the store is located.
 * @param {string} params.state - The state where the store is located.
 * @returns {string} The generated description for the Rentals detail page.
 */
export const generateRentalsDesc = ({ storeName, city, state }) => {
  if (!storeName || !city || !state) {
    return 'The Home Depot';
  }
  return `Get the tool and truck you need at The Home Depot ${storeName} with Home Depot tool rental or`
    + ` Home Depot truck rental. Whatever the job, we have what you need in ${city}, ${state}`;
};

/**
 * Generates an array of preload image objects for Rentals promotional images.
 * Includes the default Rentals banner and any active promotion image for the Rentals page.
 *
 * @param {Object} storeFinderPromotions - An object containing promotion data with image URLs, typically from emtData.
 * @returns {Array} An array of LinkTag objects for preloading promotional images.
 */
export const getRentalsPreloadImages = (storeFinderPromotions) => {
  const RENTALS_BANNER_URL = 'https://assets.homedepot-static.com/mcstore/images/rentals/hero-banner-2x.jpg';
  const images = [{ href: RENTALS_BANNER_URL }];

  const promotionImgUrl = getCurrentPromoImageSrc(
    storeFinderPromotions,
    EMT_PAGE_NAMES.RENTALS
  );
  if (promotionImgUrl) {
    images.push({ href: promotionImgUrl });
  }

  return images;
};

/**
 * Generates an array of schema objects for use in generating Rentals structured data script tags.
 * If `emtSchema` is provided, it will be used as the only schema in the array.
 * Otherwise, default schemas for breadcrumbs and FAQ are generated using the provided store and EMT content data.
 *
 * @param {Object} params - Parameters for generating schemas.
 * @param {Object} params.emtContentFileData - The EMT content file data for FAQ schema generation.
 * @param {string} [params.emtSchema] - A string containing the JSON-LD schema content for Rentals.
 * @param {Object} params.storeInfo - The store information object.
 * @returns {Array} An array of schema objects, each with a `content` (string) and `id` (string).
 */
export const getRentalsSchemas = ({ emtContentFileData, emtSchema, storeInfo }) => {
  if (emtSchema) {
    return [{ content: emtSchema, id: 'rentalsStructuredData' }];
  }
  return ([
    {
      content: buildPagePerStoreBreadcrumbs({
        storeInfo,
        departmentName: 'Tool and Truck Rental',
        departmentID: PAGE_TYPE.RENTALS
      }),
      id: 'rentalsStructuredBreadcrumb'
    },
    {
      content: buildPagePerStoreStructuredFAQ(emtContentFileData, 'rentals'),
      id: 'rentalsStructuredFAQData'
    }
  ]);
};